/**
 * Czech message catalog — mirrors the shape of `messages.en.ts` exactly;
 * `satisfies MessagesShape` makes a missing or renamed key a TypeScript
 * error. Register: vy (vykání), formal (locked in l10n/locales.md — the one
 * formal Slavic locale in this wave).
 *
 * Platform terms follow Czech UI conventions (nastavení, párování for
 * pairing, zrcadlení for notification mirroring, smazat for dismiss,
 * konverzace for conversation). "Glance" is the untranslated feature brand
 * name (glossary D1); „klip“ renders the D2 loanword in Czech script.
 *
 * Plural notes: Czech is a {one, few, many, other} CLDR locale — every
 * count-bearing `selectPlural("cs", …)` supplies all four forms. one = 1,
 * few = 2–4, many = fractional quantities only (1,5 — genitive-singular
 * noun form), other = 0 and 5+. All four always format the number.
 *
 * Out of scope: the same exclusions as the English catalog (log scopes,
 * structured event names, JSDoc, server-emitted error messages — see
 * `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Připojeno",
    statusReconnecting: "Opětovné připojování…",
    statusDisconnected: "Odpojeno",
    openSettings: "Otevřít nastavení",
    pairedFallbackDevice: "váš telefon",
    publicModeToggleEnable: "Skrýt obsah oznámení",
    publicModeToggleDisable: "Zobrazit obsah oznámení",
  },

  notifications: {
    replyAriaLabel: "Odpovědět",
    replyPlaceholder: "Odpovědět…",
    replySend: "Odeslat",
    sent: "Odesláno",
    failedToSend: "Odeslání se nezdařilo",
    unread: "Nepřečteno",
    dismiss: "Smazat",
    notificationImageAlt: "Obrázek z oznámení",
  },

  conversations: {
    replyAriaLabel: "Odpovědět",
    replyPlaceholder: "Odpovědět…",
    replySend: "Odeslat",
    sent: "Odesláno",
    failedToSend: "Odeslání se nezdařilo",
    dismiss: "Smazat konverzaci",
    messagesListLabel: "Zprávy v této konverzaci",
    deviceOwner: "Vy",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Žádné nepřečtené zprávy"
        : `${count} ${selectPlural("cs", count, {
            one: "nepřečtená zpráva",
            few: "nepřečtené zprávy",
            many: "nepřečtené zprávy",
            other: "nepřečtených zpráv",
          })}`,
  },

  list: {
    today: "Dnes",
    yesterday: "Včera",
    searchNotifications: "Hledat oznámení",
    clearSearch: "Vymazat hledání",
    clearAll: "Smazat vše",
    markAllRead: "Označit vše jako přečtené",
    groupCheckboxLabel: "Seskupit",
    noNotificationsTitle: "Zatím žádná oznámení",
    noNotificationsDesc: "Nové zprávy zrcadlené z vašeho telefonu se zobrazí zde.",
    noMatchesTitle: "Nic nenalezeno",
    notificationsList: "Oznámení",
    privacyAppList: "Oznámení podle aplikací",
    clear: "Smazat",
    clearMatches: (count: number): string =>
      `Smazat ${count} ${selectPlural("cs", count, {
        one: "shodu",
        few: "shody",
        many: "shody",
        other: "shod",
      })}`,
    noMatchesDesc: (query: string): string => `Žádná oznámení neodpovídají „${query}“.`,
    appNotificationsGroup: (appName: string): string => `Oznámení ${appName}`,
    dismissAllFrom: (appName: string): string => `Smazat vše od ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("cs", count, {
              one: "zpráva",
              few: "zprávy",
              many: "zprávy",
              other: "zpráv",
            })
          : kind === "calls"
            ? selectPlural("cs", count, {
                one: "hovor",
                few: "hovory",
                many: "hovoru",
                other: "hovorů",
              })
            : selectPlural("cs", count, {
                one: "oznámení",
                few: "oznámení",
                many: "oznámení",
                other: "oznámení",
              });
      return `${count} ${noun}`;
    },
    kbdDismiss: "smazat",
    kbdNavigate: "navigace",
    kbdOrSeparator: "nebo",
  },

  settings: {
    back: "Zpět na oznámení",
    settings: "Nastavení",
    general: "Obecné",
    theme: "Motiv",
    themeAuto: "Automatický",
    themeLight: "Světlý",
    themeDark: "Tmavý",
    language: "Jazyk",
    languageSystemDefault: "Podle systému",
    notificationsSection: "Oznámení",
    osNotifications: "Systémová oznámení",
    callNotifications: "Oznámení o hovorech",
    privacySecurity: "Soukromí",
    analyticsToggle: "Anonymní statistiky používání",
    analyticsToggleDescription: "Pouze počty a milníky — nikdy vaše oznámení.",
    statusConnected: "Připojeno",
    statusReconnecting: "Opětovné připojování…",
    statusDisconnected: "Odpojeno",
    disconnectFromPhone: "Odpojit od telefonu",
    disconnectConfirm: "Opravdu? Tím zrušíte párování zařízení.",
    yesDisconnect: "Ano, odpojit",
    cancel: "Zrušit",
    version: "Verze",
    encryption: "Šifrování",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Potřebujete pomoc? Napište na __EMAIL__",
    disconnectFailed: "Odpojení se nezdařilo — zkuste to prosím znovu",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Odpojeno, ale nepodařilo se vyčistit místní data: ${errorMsg}. Načtěte stránku znovu.`,
    disconnectLocalCleanupFailed:
      "Odpojeno, ale nepodařilo se vyčistit místní data. Načtěte stránku znovu.",
  },

  pairing: {
    enterCodeInstruction: "Zadejte tento kód v aplikaci Reflecto",
    pairedSuccessfully: "Úspěšně spárováno!",
    pairingFailed: "Párování se nezdařilo",
    requestingPairingCode: "Získávání párovacího kódu…",
    codeExpired: "Platnost kódu vypršela",
    tryAgain: "Zkusit znovu",
    failedToStartPairing: "Nepodařilo se zahájit párování",
    installHeading: "Ještě nemáte aplikaci?",
    installScanHint: "Naskenujte fotoaparátem telefonu",
    installQrLabel: "QR kód pro instalaci Reflecto do telefonu",
    capabilitiesHeading: "Po spárování můžete:",
    capabilityMirror: "Vidět tady oznámení z telefonu",
    capabilityReply: "Odpovídat, aniž byste sáhli po telefonu",
    capabilityShare: "Sdílet schránku a soubory oběma směry",
    encryptionNote: "Šifrování end-to-end. Server nikdy nevidí váš obsah.",
  },

  dnd: {
    title: "Nerušit",
    description: "Pozastavit oznámení na tomto zařízení na příští hodinu.",
    snooze1h: "Pozastavit na 1 hodinu",
    snoozeActiveUntil: (formattedTime: string): string => `Aktivní do ${formattedTime}`,
    cancelSnooze: "Zrušit",
  },

  connectors: {
    title: "Připojené služby",
    emptyBody:
      "Zatím žádné připojené služby. Připojte ChatGPT nebo Claude v nastavení jejich konektoru a poté potvrďte kód v telefonu.",
    scopeSend: "Může odesílat oznámení",
    added: (date: string): string => `Přidáno ${date}`,
    lastUsed: (relative: string): string => `Naposledy použito ${relative}`,
    neverUsed: "Nikdy nepoužito",
    revoke: "Odpojit",
    revokeConfirm: (label: string): string => `Odpojit ${label}? Okamžitě ztratí přístup.`,
    revokeAction: "Odpojit",
    revokeConfirmLabel: (label: string): string => `Odpojit ${label}`,
    cancel: "Zrušit",
    loadError: "Nepodařilo se načíst připojené služby. Zkuste to prosím znovu.",
    revokeError: "Nepodařilo se odpojit službu. Zkuste to prosím znovu.",
    retry: "Opakovat",
  },

  safetyNumber: {
    rowLabel: "Ověřit šifrování",
    rowSublabel: "Porovnat bezpečnostní čísla se svým telefonem",
    cardTitle: "Bezpečnostní číslo",
    caption: (deviceLabel: string): string =>
      `Porovnejte se číslem zobrazeným na ${deviceLabel}. Pokud si odpovídají, vaše end-to-end šifrování je neporušené.`,
    repairNote: "Toto číslo se změní, když znovu spárujete zařízení.",
    copy: "Kopírovat",
    copied: "Zkopírováno",
    maskedHint: "Skryto, když je režim ochrany soukromí zapnut",
  },

  feedback: {
    title: "Odeslat zpětnou vazbu",
    close: "Zavřít",
    messageLabel: "Vaše zpráva",
    messagePlaceholder: "Našli jste chybu, máte nápad, nebo vám něco nesedí? Napište nám.",
    categoryLabel: "Kategorie",
    categoryBug: "Chyba",
    categoryIdea: "Nápad",
    categoryOther: "Jiné",
    emailLabel: "E-mail (nepovinné, jen pokud chcete odpověď)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Nezařadíme vás do žádného seznamu.",
    disclosure:
      "Vaše zpráva se odesílá na server Reflecto a ukládá se v našem interním systému pro sledování úkolů. Přikládáme verzi aplikace a identifikátor zařízení přidělený při párování, abychom mohli hlášení propojit. Nevidíme vaše oznámení, kontakty ani žádné osobní údaje. E-mail je nepovinný — uveďte ho, jen pokud chcete odpověď. Nezařadíme vás do žádného seznamu.",
    submit: "Odeslat zpětnou vazbu",
    sending: "Odesílání…",
    success: "Děkujeme — přečte to člověk, obvykle do 24 hodin",
    error: "Odeslání se nezdařilo. Zkuste to prosím znovu.",
    done: "Hotovo",
    sendFeedback: "Odeslat zpětnou vazbu",
  },

  uninstall: {
    headline: "Rozšíření Reflecto je odinstalováno",
    body: "Nikdy jste neměli účet a rozšíření uchovávalo vaše párování jen v tomto počítači, takže po něm nezůstalo nic, co byste museli uklízet.",
    ask: "Pokud máte třicet vteřin, rádi bychom věděli, co se pokazilo.",
    categoryLegend: "O jakou zpětnou vazbu jde?",
    categoryBug: "Něco se rozbilo",
    categoryIdea: "Chybělo mi něco, co potřebuji",
    categoryOther: "Něco jiného",
    messageLabel: "Co se pokazilo?",
    messagePlaceholder: "Po několika hodinách přestala chodit oznámení.",
    messageRequired: "Nejdřív napište větu, pak odešlete.",
    charactersLeft: (count: number): string => `Zbývá ${count} znaků`,
    emailLabel: "E-mail (nepovinné)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Použijeme jen k odpovědi. K ničemu jinému.",
    submit: "Odeslat zpětnou vazbu",
    sending: "Odesílání…",
    retry: "Zkusit znovu",
    privacyNote:
      "Tento formulář není propojen s žádným zařízením. Neobsahuje žádná vaše oznámení, schránku ani soubory. E-mail je nepovinný a slouží jen k odpovědi.",
    success: "Děkujeme. Každou z těchto zpráv čte člověk.",
    errorGeneric: "Odeslání se nezdařilo. Vaše zpráva tu zůstala.",
    errorRateLimited:
      "V poslední době jste poslali několik zpráv. Zkuste to znovu asi za hodinu. Váš text tu zůstane.",
    footerWebApp: "Webová aplikace Reflecto funguje i bez rozšíření.",
    footerWebAppLink: "Otevřít webovou aplikaci",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Zavřít",
    aiSummaryTag: "Souhrn od AI",
    errorAi: "Souhrn se teď nepodařilo vytvořit.",
    retryCta: "Zkusit znovu",
    downloadHeading: "Souhrny od AI",
    downloadBody:
      "Doháníte, co vám uniklo, díky souhrnu od AI přímo v zařízení. Vše běží výhradně ve vašem zařízení.",
    downloadCta: "Stáhnout model",
    downloadPrivacyNote: "Jednorázové stažení. Server nikdy nevidí vaše oznámení.",
    downloadingHeading: "Stahování modelu…",
    caughtUp: "Máte vše přečtené — teď nic nevyžaduje vaši pozornost.",
    needsYou: "Vyžaduje pozornost",
    quiet: "Ostatní",
    scanning: (pending: number): string => `Připravuje se souhrn: ${pending}…`,
    jump: "Otevřít oznámení",
    affordanceReply: "Odpovědět",
    affordanceOpen: "Otevřít",
    affordanceCopyOtp: "Kopírovat kód",
    buttonLabel: "Glance",
  },

  update: {
    title: "Dostupná aktualizace",
    description: "Je připravena nová verze aplikace Reflecto. Aktualizujte ji načtením stránky.",
    reload: "Načíst znovu",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Toto zařízení jsme pojmenovali „${name}“.`,
    cardLooksGood: "Vypadá dobře",
    cardEdit: "Upravit",
    cardAriaLabel: "Potvrďte název zařízení",
    renameTitle: "Přejmenovat toto zařízení",
    renameLabel: "Název zařízení",
    renameSave: "Uložit",
    renameCancel: "Zrušit",
    renameFailed: "Přejmenování se nezdařilo. Zkuste to prosím znovu.",
  },

  collision: {
    title: (name: string): string => `Už máte „${name}“`,
    subtitle: "Jak toto zařízení pojmenujeme?",
    chipWork: (name: string): string => `Pracovní ${name}`,
    chipHome: (name: string): string => `Domácí ${name}`,
    chipPersonal: (name: string): string => `Osobní ${name}`,
    customLabel: "Vlastní název",
    save: "Uložit",
    useAnyway: "Přesto použít původní název",
    saveFailed: "Nový název se nepodařilo uložit. Zkuste to prosím znovu.",
    ariaLabel: "Vyřešte shodu názvů zařízení",
  },

  clipboard: {
    title: "Schránka",
    emptyTitle: "Zatím žádné klipy",
    empty: "Klipy z vašich ostatních zařízení se zobrazí zde. Klepnutím zkopírujete.",
    from: (label: string): string => `od ${label}`,
    to: (label: string): string => `pro ${label}`,
    copy: "Kopírovat do schránky",
    copied: "Zkopírováno",
    reveal: "Zobrazit",
    hide: "Skrýt",
    sensitiveChip: "Citlivé",
    cloudChip: "přes váš token API",
    maskedHint: "Obsah je skrytý — stiskněte Zobrazit",
    delete: "Smazat klip",
    sendPlaceholder: "Vložte text k odeslání…",
    sendCta: "Odeslat",
    sendSensitiveToggle: "Označit jako citlivé",
    sendSuccess: (n: number): string =>
      `Odesláno na ${n} ${selectPlural("cs", n, {
        one: "zařízení",
        few: "zařízení",
        many: "zařízení",
        other: "zařízení",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Odesláno na ${n} z ${total} ${selectPlural("cs", total, {
        one: "zařízení",
        few: "zařízení",
        many: "zařízení",
        other: "zařízení",
      })}.`,
    sendNoDevices: "Žádná spárovaná zařízení k odeslání.",
    sendFailed: "Odeslání se nezdařilo. Zkuste to prosím znovu.",
    sendEmpty: "Není co odeslat.",
    dockRecentHeading: "Nedávné",
    dockEmptyClipboard: "Vaše schránka je prázdná.",
    clearAll: "Smazat vše",
    clearAllConfirm: "Ano, smazat vše",
    clearAllCancel: "Zrušit",
    shareTitle: "Odeslat na vaše zařízení",
    shareReview:
      "Zkontrolujte, co odesíláte. Data poputují na vaše spárovaná zařízení se šifrováním end-to-end.",
    shareSend: "Odeslat",
    shareCancel: "Zrušit",
    shareNotPaired: "Chcete-li odeslat sdílený text, nejprve spárujte zařízení.",
    shareOpenApp: "Otevřít Reflecto",
    pasteFromClipboard: "Vložit ze schránky",
    readFailed: "Schránku se nepodařilo přečíst — vložte text níže.",
  },
  install: {
    title: "Nainstalovat Reflecto",
    body: "Přidejte na plochu pro rychlejší přístup.",
    cta: "Nainstalovat",
    dismissAria: "Zavřít výzvu k instalaci",
  },
  nav: {
    home: "Domů",
    clipboard: "Schránka",
    files: "Soubory",
    ariaLabel: "Hlavní",
    fileHiddenPublicMode: "Skryto, dokud je zapnutý režim soukromí.",
  },
  common: {
    loading: "Načítání",
  },
  errors: {
    notPaired: "Nespárováno",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} na ${os}`,
    webFallback: "Webový prohlížeč",
  },

  palette: {
    placeholder: "Zadejte příkaz…",
    noResults: "Žádné odpovídající příkazy",
    ariaLabel: "Paleta příkazů",
    clearAll: "Smazat všechna oznámení",
    glance: "Souhrn Glance",
    cancelSnooze: "Zrušit pozastavení",
    copyClip: "Kopírovat poslední přijatý klip",
  },

  fileTransfer: {
    sendTitle: "Odeslat soubor",
    sendChooseFile: "Vybrat soubor",
    sendDeviceLabel: "Odeslat na",
    sendPhoneFallbackName: "Telefon",
    send: "Odeslat soubor",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Připravování…" : `Připravování — ${pct}%`,
    sendDelivering: "Doručování do vašeho telefonu…",
    sendWaitingAccept: "Čeká se, až váš telefon soubor přijme…",
    sendWaitingAppOpen: "Otevřete Reflecto v telefonu a soubor přijměte",
    sendWaitingAppOpenHint:
      "Váš telefon nemůže přijímat push oznámení, takže tento soubor zobrazí, až bude Reflecto otevřené.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Nahrávání části ${partNumber} z ${partCount}…`,
    sendDone: "Odesláno.",
    sendFailedRejected: "Váš telefon soubor odmítl.",
    sendFailedNoAnswer: "Váš telefon neodpověděl.",
    sendFailedUploadFailed: "Nahrání se nezdařilo. Zkuste to prosím znovu.",
    sendFailedCancelled: "Zrušeno.",
    sendFailedGeneric: "Něco se pokazilo. Zkuste to prosím znovu.",
    sendTooLarge: (maxMiB: number): string => `Soubor je příliš velký (max. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Nechte tuto kartu otevřenou, dokud přenos neskončí.",
    sendAnother: "Odeslat další",
    sendNoDevice: "Chcete-li odesílat soubory, nejprve spárujte telefon.",
    sendCancel: "Zrušit",
    sendPeerProgress: (chunksDone: number): string => `Fragmenty přijaté telefonem: ${chunksDone}.`,

    receiveTitle: "Příchozí soubory",
    receiveOfferFrom: (sender: string): string => `${sender} vám chce poslat soubor`,
    receiveAccept: "Přijmout",
    receiveDecline: "Odmítnout",
    receiveReceiving: (n: number, total: number): string => `Příjem části ${n} z ${total}…`,
    receiveDone: "Uloženo.",
    receiveFailed: "Přenos se nezdařil.",
    receiveCancelled: "Zrušeno.",
    receiveCancel: "Zrušit",

    errorChunkAuthFail: "Přenos se nezdařil — data se nepodařilo ověřit. Zkuste to znovu.",
    errorHashMismatch: "Přenos se nezdařil — soubor neprošel kontrolou integrity. Zkuste to znovu.",
    errorStorageFull: "Pro tento přenos není dostatek úložného prostoru.",
    errorExpired: "Platnost přenosu vypršela.",
    errorQuotaExceeded: "Denní limit přenosů byl dosažen — zkuste to znovu zítra.",
    errorInternal: "Přenos se nezdařil — něco se pokazilo.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Zavřít",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Průběh přenosu",
    outgoing: (n: number): string => `Odesílané (${n})`,
    incoming: (n: number): string => `Přijímané (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Přijmout vše",
    dropTitle: "Přetáhněte sem",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Soubory až do ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Přetáhněte sem nebo vyberte soubor k odeslání",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Přímo",
    transportDirectHint: "Odesláno přímo mezi zařízeními — nikdy uloženo na našich serverech.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Přes server",
    transportRelayHint: "Přeneseno přes naše servery, stále koncově šifrované — nemůžeme to číst.",
    directBadge: "Přímo",
  },
  time: {
    justNow: "právě teď",
    minutesAgo: (count: number): string => `před ${count} min`,
    hoursAgo: (count: number): string => `před ${count} h`,
  },
} satisfies MessagesShape;
